import React, { useEffect, useMemo, useState } from 'react';
import { Image, Platform, Pressable, StyleSheet, Text, TextInput, View } from 'react-native';
import type { AccountSraDto, AccountUserSettingDto } from '../../../types/api';
import {
  ACCESS_TYPE_10_ALLOWED_SETTINGS,
  SRA_ACCESS_COLUMNS,
  applySraEnableMapToConfigInfo,
  applySraToggleToEnableMap,
  getSraEnableWithDefault,
  sraSettingsToEnableMap,
} from '../../../services/account/SraMenuAccess';
import { OutlineCheckboxBox, renderWebOutlineCheckbox } from '../../OutlineCheckbox';
import type { OrderedOption } from './OrderedDualListSelector';
import {
  applyOfficeOnlineSettings,
  officeOnlineDraftsEqual,
  parseOfficeOnlineSettings,
  resolveEnabledAssessmentIds,
  type OfficeOnlineDraft,
} from '../../../services/account/SraOfficeSettings';
import { resolveCurrentBaseUrl } from '../../../services/clients/ClientLaunchBuilder';
import { Base64ImageStorage, extractImgSrc } from '../../../storage/Base64ImageStorage';
import { ConfigManager } from '../../../config/configManager';
import { EDITOR_COLORS, editorText, webControlStyle } from '../../../styles/editorTheme';

interface ToggleArrowIcons {
  upSrc: string | null;
  downSrc: string | null;
  loaded: boolean;
}

interface SiteOption {
  siteId: string;
  siteName: string;
}

interface MasterReassignOption {
  sraId: string;
  label: string;
}

interface SraRowEditorProps {
  row: AccountSraDto;
  expanded: boolean;
  lastLogin: string;
  accessTypeLabel?: string;
  onToggle: () => void;
  onConfigInfoChange?: (row: AccountSraDto, configInfo: AccountUserSettingDto[]) => void;
  onUpdateSra?: (nextRow: AccountSraDto, options?: { reassignMasterToSraId?: string; sendInviteBaseUrl?: string }) => Promise<void>;
  siteOptions: SiteOption[];
  currentLoggedInSraId?: string;
  currentLoggedInSraEmail?: string;
  /** Session AccessType — an email match only identifies the signed-in user's row within the same access-type family. */
  currentLoggedInAccessType?: string;
  isMasterAccount?: boolean;
  canReassignMaster?: boolean;
  masterReassignCandidates?: MasterReassignOption[];
  /**
   * The practice's assessments (those selected in Account Settings > Manage assessments), in
   * practice order — an Office Online (AccessType 30) access enables/disables from this list.
   */
  assessmentOptions?: OrderedOption[];
}

// SRA AccessType for Office Online users.
const OFFICE_ONLINE_ACCESS_TYPE = 30;

// Account-admin accesses (AccessType 20 / 21) carry a password in ConfigInfo.
const SRA_PASSWORD_SETTING = 'srapassword';

const isPasswordAccessType = (accessType: string | null | undefined): boolean => {
  const parsed = Number.parseInt((accessType || '').trim(), 10);
  return parsed === 20 || parsed === 21;
};

const readSraPassword = (configInfo: AccountUserSettingDto[] | undefined): string => {
  const entry = (configInfo ?? []).find((setting) => (setting.Name || '').trim().toLowerCase() === SRA_PASSWORD_SETTING);
  return entry?.Value ?? '';
};

const writeSraPassword = (configInfo: AccountUserSettingDto[], password: string): AccountUserSettingDto[] => {
  const index = configInfo.findIndex((setting) => (setting.Name || '').trim().toLowerCase() === SRA_PASSWORD_SETTING);
  if (index < 0) {
    return [...configInfo, { Name: SRA_PASSWORD_SETTING, Value: password }];
  }
  return configInfo.map((setting, i) => (i === index ? { ...setting, Value: password } : setting));
};

const UP_ARROW_IMAGE_ID = '101';
const DOWN_ARROW_IMAGE_ID = '100';

let cachedToggleArrowIcons: ToggleArrowIcons | null = null;
let toggleArrowIconsLoadPromise: Promise<ToggleArrowIcons> | null = null;

const EMPTY_TOGGLE_ARROWS: ToggleArrowIcons = {
  upSrc: null,
  downSrc: null,
  loaded: false,
};

const resolveRuntimeStorage = () => {
  return (globalThis as any).__proGroupsStorage
    ?? (globalThis as any).__childNavStorage
    ?? null;
};

const resolveImageSrc = (stored: string | null | undefined): string | null => {
  if (!stored) return null;

  const trimmed = stored.trim();
  if (!trimmed) return null;

  const extracted = extractImgSrc(trimmed);
  if (extracted) {
    return extracted;
  }

  if (trimmed.startsWith('data:image/')) {
    return trimmed;
  }

  if (/^[A-Za-z0-9+/=\s]+$/.test(trimmed) && trimmed.length > 80) {
    return `data:image/png;base64,${trimmed.replace(/\s+/g, '')}`;
  }

  return null;
};

const loadToggleArrowIcons = async (): Promise<ToggleArrowIcons> => {
  if (cachedToggleArrowIcons && (cachedToggleArrowIcons.downSrc || cachedToggleArrowIcons.upSrc)) {
    return cachedToggleArrowIcons;
  }

  if (toggleArrowIconsLoadPromise) {
    return toggleArrowIconsLoadPromise;
  }

  toggleArrowIconsLoadPromise = (async () => {
    try {
      const storage = resolveRuntimeStorage();
      if (!storage) {
        return { ...EMPTY_TOGGLE_ARROWS, loaded: false };
      }

      const imageStorage = new Base64ImageStorage(storage);
      const [downArrow, upArrow] = await Promise.all([
        imageStorage.getImage(DOWN_ARROW_IMAGE_ID),
        imageStorage.getImage(UP_ARROW_IMAGE_ID),
      ]);

      const nextIcons: ToggleArrowIcons = {
        downSrc: resolveImageSrc(downArrow?.base64),
        upSrc: resolveImageSrc(upArrow?.base64),
        loaded: true,
      };

      if (nextIcons.downSrc || nextIcons.upSrc) {
        cachedToggleArrowIcons = nextIcons;
      }

      return nextIcons;
    } catch (error) {
      console.warn('SraRowEditor: failed to load toggle arrows', error);
      return { ...EMPTY_TOGGLE_ARROWS, loaded: false };
    } finally {
      toggleArrowIconsLoadPromise = null;
    }
  })();

  return toggleArrowIconsLoadPromise;
};

const removeAccessTypeSuffix = (label: string): string => {
  return label.replace(/\s*\([^)]*\)\s*$/, '').trim();
};

const MASTER_REQUIRED_SETTINGS = ['enable_accountmenu', 'enable_manageteams'];

const isMasterRequiredSetting = (settingName: string): boolean => {
  const normalized = settingName.trim().toLowerCase();
  return MASTER_REQUIRED_SETTINGS.some((required) => required.toLowerCase() === normalized);
};

// The allowed set itself lives in SraMenuAccess (ACCESS_TYPE_10_ALLOWED_SETTINGS) so the menu
// visibility rule and this editor can't drift apart.
const ACCESS_TYPE_10_FORCE_DISABLED = [
  'enable_assessmentoptions',
  'enable_viewopenadmins',
  'enable_viewflags',
  'enable_viewmessaging',
  'enable_scheduledstats',
  'enable_systemstats',
  'enable_dataexport',
  'enable_managetouchpoints',
  'enable_manageteams',
];

const isAllowedForAccessType = (settingName: string, accessType: string | null | undefined): boolean => {
  const parsed = Number.parseInt((accessType || '').trim(), 10);
  if (parsed !== 10) {
    return true;
  }
  const normalized = settingName.trim().toLowerCase();
  return ACCESS_TYPE_10_ALLOWED_SETTINGS.some((allowed) => allowed.toLowerCase() === normalized);
};

const enforceAccessTypeRestrictions = (
  configInfo: AccountUserSettingDto[],
  accessType: string | null | undefined
): AccountUserSettingDto[] => {
  const parsed = Number.parseInt((accessType || '').trim(), 10);
  if (parsed !== 10) {
    return configInfo;
  }

  const normalized = new Set(ACCESS_TYPE_10_FORCE_DISABLED.map((name) => name.trim().toLowerCase()));
  return configInfo.map((setting) => {
    const settingName = (setting.Name || '').trim().toLowerCase();
    if (normalized.has(settingName)) {
      return { ...setting, Value: '0' };
    }
    return setting;
  });
};

export const SraRowEditor: React.FC<SraRowEditorProps> = ({
  row,
  expanded,
  lastLogin,
  accessTypeLabel,
  onToggle,
  onConfigInfoChange,
  onUpdateSra,
  siteOptions,
  currentLoggedInSraId,
  currentLoggedInSraEmail,
  currentLoggedInAccessType,
  isMasterAccount = false,
  canReassignMaster = false,
  masterReassignCandidates = [],
  assessmentOptions = [],
}) => {
  const [toggleArrowIcons, setToggleArrowIcons] = useState<ToggleArrowIcons>(cachedToggleArrowIcons ?? EMPTY_TOGGLE_ARROWS);
  const [nameValue, setNameValue] = useState(row.Name || '');
  const [emailValue, setEmailValue] = useState(row.Email || '');
  const [configEnableState, setConfigEnableState] = useState<Record<string, boolean>>(() => sraSettingsToEnableMap(row.ConfigInfo));
  const isOfficeOnline = Number.parseInt((row.AccessType || '').trim(), 10) === OFFICE_ONLINE_ACCESS_TYPE;
  const [officeDraft, setOfficeDraft] = useState<OfficeOnlineDraft>(() => parseOfficeOnlineSettings(row.ConfigInfo));
  // The practice's assessments (Account Settings > Manage assessments), in practice order.
  const practiceAssessmentIds = useMemo(() => assessmentOptions.map((option) => option.id), [assessmentOptions]);
  const enabledAssessmentIds = useMemo(
    () => new Set(resolveEnabledAssessmentIds(officeDraft, practiceAssessmentIds)),
    [officeDraft, practiceAssessmentIds]
  );
  const [selectedSiteId, setSelectedSiteId] = useState((row.SiteId || '').trim());
  const hasPassword = isPasswordAccessType(row.AccessType);
  const [passwordValue, setPasswordValue] = useState(() => readSraPassword(row.ConfigInfo));
  const [reassignMasterToSraId, setReassignMasterToSraId] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const rowIdentity = `${row.SraId || ''}:${row.SiteId || ''}:${row.Email || ''}`;
  const isWeb = Platform.OS === 'web';

  useEffect(() => {
    let cancelled = false;

    const refreshToggleArrows = async () => {
      const icons = await loadToggleArrowIcons();
      if (!cancelled) {
        setToggleArrowIcons(icons);
      }
    };

    void refreshToggleArrows();

    const removeListener = ConfigManager.addLogoImageListener(() => {
      void refreshToggleArrows();
    });

    return () => {
      cancelled = true;
      removeListener();
    };
  }, []);

  useEffect(() => {
    setNameValue(row.Name || '');
    setEmailValue(row.Email || '');
    setConfigEnableState(sraSettingsToEnableMap(row.ConfigInfo));
    setOfficeDraft(parseOfficeOnlineSettings(row.ConfigInfo));
    setSelectedSiteId((row.SiteId || '').trim());
    setPasswordValue(readSraPassword(row.ConfigInfo));
    setReassignMasterToSraId('');
    setUpdateError(null);
  }, [rowIdentity]);

  const resolvedSiteOptions = useMemo(() => {
    const rowSiteId = (row.SiteId || '').trim();
    if (!rowSiteId || siteOptions.some((entry) => entry.siteId === rowSiteId)) {
      return siteOptions;
    }

    return [{ siteId: rowSiteId, siteName: rowSiteId }, ...siteOptions];
  }, [row.SiteId, siteOptions]);

  const filteredColumns = useMemo(() => {
    return SRA_ACCESS_COLUMNS.map((column) => ({
      ...column,
      items: column.items.filter((item) => isAllowedForAccessType(item.settingName, row.AccessType)),
    }));
  }, [row.AccessType]);

  const maxEditorRows = Math.max(...filteredColumns.map((column) => column.items.length));

  const getEnabled = (settingName: string): boolean => getSraEnableWithDefault(configEnableState, settingName, true);

  const updateSetting = (settingName: string, nextValue: boolean) => {
    setConfigEnableState((current) => {
      const next = applySraToggleToEnableMap(current, settingName, nextValue);

      onConfigInfoChange?.(row, applySraEnableMapToConfigInfo(row.ConfigInfo, next));
      return next;
    });
  };

  const renderEnableCheckbox = (
    checked: boolean,
    onToggle: (nextChecked: boolean) => void,
    accessibilityLabel: string,
    disabled = false
  ) => {
    if (isWeb) {
      return renderWebOutlineCheckbox(checked, onToggle, accessibilityLabel, disabled);
    }

    return (
      <Pressable
        onPress={() => onToggle(!checked)}
        style={styles.checkboxTouch}
        accessibilityRole="checkbox"
        accessibilityState={{ checked, disabled }}
        accessibilityLabel={accessibilityLabel}
        disabled={disabled}
      >
        <OutlineCheckboxBox checked={checked} disabled={disabled} />
      </Pressable>
    );
  };

  // Menu-access enables as ConfigInfo — what the settings table edits for every non-Office access.
  const enablesConfigInfo = useMemo(
    () => applySraEnableMapToConfigInfo(row.ConfigInfo, configEnableState),
    [configEnableState, row.ConfigInfo]
  );

  // Office Online users also carry their assessment list + access code / launch list / main menu
  // settings in ConfigInfo (see SraOfficeSettings). Saving writes all of them explicitly.
  const editedConfigInfo = useMemo(() => {
    const withOffice = isOfficeOnline
      ? applyOfficeOnlineSettings(enablesConfigInfo, officeDraft, practiceAssessmentIds)
      : enablesConfigInfo;
    return hasPassword ? writeSraPassword(withOffice, passwordValue) : withOffice;
  }, [enablesConfigInfo, hasPassword, isOfficeOnline, officeDraft, passwordValue, practiceAssessmentIds]);

  const officeDirty = isOfficeOnline
    && !officeOnlineDraftsEqual(officeDraft, parseOfficeOnlineSettings(row.ConfigInfo), practiceAssessmentIds);

  const nameDirty = nameValue.trim() !== (row.Name || '').trim();
  const emailDirty = emailValue.trim() !== (row.Email || '').trim();
  const siteDirty = selectedSiteId !== (row.SiteId || '').trim();
  // Compared against the menu enables only (not the Office settings, which have defaults that
  // aren't stored until the first save) plus the Office draft-vs-stored check above.
  const passwordDirty = hasPassword && passwordValue !== readSraPassword(row.ConfigInfo);
  const configDirty = JSON.stringify(enablesConfigInfo) !== JSON.stringify(row.ConfigInfo ?? []) || officeDirty || passwordDirty;
  const parsedStatus = Number.parseInt((row.Status || '').trim(), 10);
  const isDisabledStatus = Number.isFinite(parsedStatus)
    ? parsedStatus <= 0
    : (row.Status || '').trim() === '0';
  const isDeletedStatus = Number.isFinite(parsedStatus) && parsedStatus <= -2;
  const formReadOnly = isDisabledStatus || isSaving;
  const isCurrentLoggedInSra = useMemo(() => {
    const rowSraId = (row.SraId || '').trim();
    const rowEmail = (row.Email || '').trim().toLowerCase();
    const currentSraId = (currentLoggedInSraId || '').trim();
    const currentEmail = (currentLoggedInSraEmail || '').trim().toLowerCase();

    if (rowSraId && currentSraId && rowSraId === currentSraId) {
      return true;
    }

    if (rowEmail && currentEmail && rowEmail === currentEmail) {
      // The same email can hold several accesses (e.g. Pro login + an Office Online access);
      // only the one in the signed-in user's access-type family is "this" row.
      const rowType = Number.parseInt((row.AccessType || '').trim(), 10);
      const sessionType = Number.parseInt((currentLoggedInAccessType || '').trim(), 10);
      if (Number.isFinite(rowType) && Number.isFinite(sessionType)
        && Math.floor(rowType / 10) !== Math.floor(sessionType / 10)) {
        return false;
      }
      return true;
    }

    return false;
  }, [currentLoggedInAccessType, currentLoggedInSraEmail, currentLoggedInSraId, row.AccessType, row.Email, row.SraId]);

  // Disable / Delete Access are offered for every access except the master. The one other
  // exclusion is the exact record the signed-in session belongs to (SraId equality) — NOT the
  // email heuristic above, which also matches a person's other accesses under the same email.
  const isOwnSessionRecord = useMemo(() => {
    const rowSraId = (row.SraId || '').trim();
    const currentSraId = (currentLoggedInSraId || '').trim();
    return !!rowSraId && !!currentSraId && rowSraId === currentSraId;
  }, [currentLoggedInSraId, row.SraId]);
  const canChangeAccessStatus = !isMasterAccount && !isOwnSessionRecord;

  const reassignDirty = isMasterAccount && isCurrentLoggedInSra && canReassignMaster && !!reassignMasterToSraId.trim();
  const isDirty = nameDirty || emailDirty || siteDirty || configDirty || reassignDirty;

  const resolveSiteNameById = (siteId: string): string => {
    return resolvedSiteOptions.find((entry) => entry.siteId === siteId)?.siteName || siteId;
  };

  const cycleSite = () => {
    if (resolvedSiteOptions.length === 0) {
      return;
    }

    const currentIndex = resolvedSiteOptions.findIndex((entry) => entry.siteId === selectedSiteId);
    const nextIndex = currentIndex < 0 || currentIndex === resolvedSiteOptions.length - 1 ? 0 : currentIndex + 1;
    setSelectedSiteId(resolvedSiteOptions[nextIndex].siteId);
  };

  const cycleReassignMasterTarget = () => {
    if (masterReassignCandidates.length === 0) {
      return;
    }

    const ordered = [''].concat(masterReassignCandidates.map((entry) => entry.sraId));
    const currentIndex = ordered.indexOf(reassignMasterToSraId);
    const nextIndex = currentIndex < 0 || currentIndex === ordered.length - 1 ? 0 : currentIndex + 1;
    setReassignMasterToSraId(ordered[nextIndex]);
  };

  const webSelectStyle = { ...webControlStyle, width: '220px', height: 28 } as any;

  const onUpdate = async () => {
    if (!onUpdateSra || !isDirty || formReadOnly) {
      return;
    }

    const finalConfigInfo = enforceAccessTypeRestrictions(editedConfigInfo, row.AccessType);

    const nextRow: AccountSraDto = {
      ...row,
      Name: nameValue.trim(),
      Email: emailValue.trim(),
      SiteId: selectedSiteId,
      ConfigInfo: finalConfigInfo,
    };

    try {
      setIsSaving(true);
      setUpdateError(null);
      await onUpdateSra(nextRow, {
        reassignMasterToSraId: isMasterAccount && isCurrentLoggedInSra && reassignMasterToSraId.trim()
          ? reassignMasterToSraId.trim()
          : undefined,
      });
      setReassignMasterToSraId('');
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setUpdateError(message);
    } finally {
      setIsSaving(false);
    }
  };

  // Send Invite: saves the form as it stands (the invitation goes to the email shown) and asks the
  // backend to send the invitation in the same provider_sra_put. PracSraUpdateDto.SendInvite
  // carries the base URL the browser is pointing at — the sign-in URL the email links to.
  const onSendInvite = async () => {
    if (!onUpdateSra || isSaving || isDisabledStatus || !emailValue.trim()) {
      return;
    }

    const signInBaseUrl = resolveCurrentBaseUrl();
    if (!signInBaseUrl) {
      setUpdateError('Could not determine the site address to include in the invitation.');
      return;
    }

    const finalConfigInfo = enforceAccessTypeRestrictions(editedConfigInfo, row.AccessType);
    const nextRow: AccountSraDto = {
      ...row,
      Name: nameValue.trim(),
      Email: emailValue.trim(),
      SiteId: selectedSiteId,
      ConfigInfo: finalConfigInfo,
    };

    try {
      setIsSaving(true);
      setUpdateError(null);
      await onUpdateSra(nextRow, { sendInviteBaseUrl: signInBaseUrl });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setUpdateError(message);
    } finally {
      setIsSaving(false);
    }
  };

  const onToggleAccessStatus = async () => {
    if (!onUpdateSra || isSaving || isMasterAccount) {
      return;
    }

    const nextStatus = isDisabledStatus ? '1' : '0';
    const finalConfigInfo = enforceAccessTypeRestrictions(editedConfigInfo, row.AccessType);
    const nextRow: AccountSraDto = {
      ...row,
      Name: nameValue.trim(),
      Email: emailValue.trim(),
      SiteId: selectedSiteId,
      ConfigInfo: finalConfigInfo,
      Status: nextStatus,
    };

    try {
      setIsSaving(true);
      setUpdateError(null);
      await onUpdateSra(nextRow);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setUpdateError(message);
    } finally {
      setIsSaving(false);
    }
  };

  const onDeleteAccess = async () => {
    if (!onUpdateSra || isSaving || isDeletedStatus || isMasterAccount) {
      return;
    }

    const finalConfigInfo = enforceAccessTypeRestrictions(editedConfigInfo, row.AccessType);
    const nextRow: AccountSraDto = {
      ...row,
      Name: nameValue.trim(),
      Email: emailValue.trim(),
      SiteId: selectedSiteId,
      ConfigInfo: finalConfigInfo,
      Status: '-2',
    };

    try {
      setIsSaving(true);
      setUpdateError(null);
      await onUpdateSra(nextRow);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setUpdateError(message);
    } finally {
      setIsSaving(false);
    }
  };

  const activeArrowSrc = expanded ? toggleArrowIcons.upSrc : toggleArrowIcons.downSrc;
  const resolvedAccessTypeLabelBase = accessTypeLabel?.trim() || row.AccessType || '-';
  const resolvedAccessTypeLabel = isMasterAccount && !resolvedAccessTypeLabelBase.includes('*')
    ? `${resolvedAccessTypeLabelBase} *`
    : resolvedAccessTypeLabelBase;

  return (
    <>
      <View style={[styles.row, expanded && styles.rowExpanded]}>
        <View style={styles.toggleCol}>
          <Pressable onPress={onToggle} style={styles.expandButton} accessibilityRole="button" accessibilityLabel="Toggle SRA row details">
            {activeArrowSrc ? (
              <Image source={{ uri: activeArrowSrc }} style={styles.expandImage} resizeMode="contain" />
            ) : (
              <Text style={styles.expandButtonText}>{expanded ? '▲' : '▼'}</Text>
            )}
          </Pressable>
        </View>

        <View style={styles.nameCol}>
          {expanded ? (
            <TextInput
              style={[styles.inlineInput, formReadOnly && styles.inlineInputDisabled]}
              value={nameValue}
              onChangeText={setNameValue}
              editable={!formReadOnly}
              autoCapitalize="words"
              placeholder="Name"
              placeholderTextColor={EDITOR_COLORS.placeholder}
            />
          ) : (
            <Text style={[styles.primaryText, isDisabledStatus && styles.primaryTextDisabled]}>{row.Name || '-'}</Text>
          )}
        </View>

        <View style={styles.emailCol}>
          {expanded ? (
            <TextInput
              style={[styles.inlineInput, formReadOnly && styles.inlineInputDisabled]}
              value={emailValue}
              onChangeText={setEmailValue}
              editable={!formReadOnly}
              autoCapitalize="none"
              keyboardType="email-address"
              placeholder="Email"
              placeholderTextColor={EDITOR_COLORS.placeholder}
            />
          ) : (
            <Text style={[styles.primaryText, isDisabledStatus && styles.primaryTextDisabled]}>{row.Email || '-'}</Text>
          )}
        </View>

        <View style={styles.lastLoginCol}>
          <Text style={styles.primaryText}>{lastLogin}</Text>
        </View>

        <View style={styles.accessTypeCol}>
          <Text style={styles.primaryText}>{resolvedAccessTypeLabel}</Text>
        </View>
      </View>

      {expanded ? (
        <View style={styles.editPanel}>
          {/* Action menu on the left, content to its right — same layout as the rest of the app. */}
          <View style={styles.editPanelMenu}>
            {isDisabledStatus ? (
              canChangeAccessStatus ? (
                <Pressable
                  style={[styles.menuLinkBtn, styles.menuLinkBtnDelete, (isSaving || isDeletedStatus || isMasterAccount) && styles.menuLinkBtnDisabled]}
                  accessibilityRole="button"
                  accessibilityLabel="Delete Access"
                  onPress={onDeleteAccess}
                  disabled={isSaving || isDeletedStatus || isMasterAccount}
                >
                  <Text style={[styles.menuLinkText, styles.menuLinkTextDanger]}>
                    {isDeletedStatus ? 'Access Deleted' : (isSaving ? 'Deleting...' : 'Delete Access')}
                  </Text>
                </Pressable>
              ) : null
            ) : (
              <Pressable
                style={[styles.menuLinkBtn, (isSaving || !emailValue.trim()) && styles.menuLinkBtnDisabled]}
                accessibilityRole="button"
                accessibilityLabel="Send Invite"
                onPress={onSendInvite}
                disabled={isSaving || !emailValue.trim()}
              >
                <Text style={styles.menuLinkText}>{isSaving ? 'Sending...' : 'Send Invite'}</Text>
              </Pressable>
            )}
            {canChangeAccessStatus ? (
              <Pressable
                style={[styles.menuLinkBtn, styles.menuLinkBtnStatus, isSaving && styles.menuLinkBtnDisabled]}
                accessibilityRole="button"
                accessibilityLabel={isDisabledStatus ? 'Re-enable Access' : 'Disable Access'}
                onPress={onToggleAccessStatus}
                disabled={isSaving}
              >
                <Text style={styles.menuLinkText}>{isDisabledStatus ? 'Re-enable Access' : 'Disable Access'}</Text>
              </Pressable>
            ) : null}
            {!isDisabledStatus ? (
              <Pressable
                style={[styles.menuLinkBtn, styles.menuLinkBtnUpdate, (!isDirty || isSaving) && styles.menuLinkBtnDisabled]}
                accessibilityRole="button"
                accessibilityLabel="Update SRA"
                onPress={onUpdate}
                disabled={!isDirty || formReadOnly}
              >
                <Text style={styles.menuLinkText}>{isSaving ? 'Updating...' : 'Update'}</Text>
              </Pressable>
            ) : null}
            {updateError ? <Text style={styles.updateErrorText}>{updateError}</Text> : null}
          </View>

          <View style={styles.editPanelLeft}>

            {isOfficeOnline ? (
              // Office Online: two columns of checkboxes instead of the menu-access table —
              // the practice's assessments (all enabled by default) and this access's settings.
              <View style={styles.officeColumns}>
                <View style={styles.officeColumn}>
                  <Text style={styles.officeColumnHeading}>Assessments</Text>
                  {assessmentOptions.length === 0 ? (
                    <Text style={styles.officeEmptyText}>No assessments are enabled for this practice.</Text>
                  ) : assessmentOptions.map((option) => (
                    <View key={option.id} style={styles.officeCheckRow}>
                      {renderEnableCheckbox(
                        enabledAssessmentIds.has(option.id),
                        (nextChecked) => {
                          const current = resolveEnabledAssessmentIds(officeDraft, practiceAssessmentIds);
                          const next = nextChecked
                            ? [...current, option.id]
                            : current.filter((id) => id !== option.id);
                          setOfficeDraft((draft) => ({ ...draft, assessmentIds: next }));
                        },
                        option.label,
                        formReadOnly
                      )}
                      <Text style={[styles.officeCheckLabel, formReadOnly && styles.accessItemLabelDisabled]}>{option.label}</Text>
                    </View>
                  ))}
                </View>

                <View style={styles.officeColumn}>
                  <Text style={styles.officeColumnHeading}>Settings</Text>
                  <View style={styles.officeCheckRow}>
                    {renderEnableCheckbox(
                      officeDraft.enableAccessCode,
                      (nextChecked) => setOfficeDraft((draft) => ({ ...draft, enableAccessCode: nextChecked })),
                      'Enable access code',
                      formReadOnly
                    )}
                    <Text style={[styles.officeCheckLabel, formReadOnly && styles.accessItemLabelDisabled]}>Enable access code</Text>
                    <TextInput
                      style={[
                        styles.officeCodeInput,
                        (!officeDraft.enableAccessCode || formReadOnly) && styles.inlineInputDisabled,
                      ]}
                      value={officeDraft.accessCode}
                      onChangeText={(text) => setOfficeDraft((draft) => ({ ...draft, accessCode: text }))}
                      editable={officeDraft.enableAccessCode && !formReadOnly}
                      autoCapitalize="none"
                      autoCorrect={false}
                      accessibilityLabel="Access code"
                    />
                  </View>
                  <View style={styles.officeCheckRow}>
                    {renderEnableCheckbox(
                      officeDraft.enableLaunchList,
                      (nextChecked) => setOfficeDraft((draft) => ({ ...draft, enableLaunchList: nextChecked })),
                      'Enable launch list',
                      formReadOnly
                    )}
                    <Text style={[styles.officeCheckLabel, formReadOnly && styles.accessItemLabelDisabled]}>Enable launch list</Text>
                  </View>
                  <View style={styles.officeCheckRow}>
                    {renderEnableCheckbox(
                      officeDraft.enableMainMenu,
                      (nextChecked) => setOfficeDraft((draft) => ({ ...draft, enableMainMenu: nextChecked })),
                      'Enable main menu',
                      formReadOnly
                    )}
                    <Text style={[styles.officeCheckLabel, formReadOnly && styles.accessItemLabelDisabled]}>Enable main menu</Text>
                  </View>
                </View>
              </View>
            ) : (
            <View style={styles.accessTableWrap}>
              <View style={styles.accessHeaderRow}>
                {filteredColumns.map((column, columnIndex) => {
                  const columnEnabled = getEnabled(column.menuSettingName);
                  return (
                    <React.Fragment key={column.section}>
                      <View style={styles.accessHeaderCell}>
                        {renderEnableCheckbox(
                          columnEnabled,
                          (nextChecked) => updateSetting(column.menuSettingName, nextChecked),
                          `Toggle ${column.section} menu`,
                          formReadOnly || (isMasterAccount && isMasterRequiredSetting(column.menuSettingName))
                        )}
                        <Text style={styles.accessHeaderLabel}>{column.section}</Text>
                      </View>
                      {columnIndex < filteredColumns.length - 1 ? <View style={styles.accessSpacerCol} /> : null}
                    </React.Fragment>
                  );
                })}
              </View>

              {Array.from({ length: maxEditorRows }).map((_, rowIndex) => (
                <View key={`access-row-${rowIndex}`} style={styles.accessDataRow}>
                  {filteredColumns.map((column, columnIndex) => {
                    const item = column.items[rowIndex];
                    const columnEnabled = getEnabled(column.menuSettingName);

                    if (!item) {
                      return (
                        <React.Fragment key={`${column.section}-empty-${rowIndex}`}>
                          <View style={styles.accessDataCell} />
                          {columnIndex < SRA_ACCESS_COLUMNS.length - 1 ? <View style={styles.accessSpacerCol} /> : null}
                        </React.Fragment>
                      );
                    }

                    const rowEnabled = getEnabled(item.settingName);
                    const disabled = !columnEnabled || formReadOnly || (isMasterAccount && isMasterRequiredSetting(item.settingName));
                    return (
                      <React.Fragment key={`${column.section}-${item.settingName}`}>
                        <View style={styles.accessDataCell}>
                          {renderEnableCheckbox(
                            rowEnabled,
                            (nextChecked) => updateSetting(item.settingName, nextChecked),
                            `Toggle ${item.label}`,
                            disabled
                          )}
                          <Text style={[styles.accessItemLabel, disabled && styles.accessItemLabelDisabled]}>{item.label}</Text>
                        </View>
                        {columnIndex < filteredColumns.length - 1 ? <View style={styles.accessSpacerCol} /> : null}
                      </React.Fragment>
                    );
                  })}
                </View>
              ))}
            </View>
            )}

            <View style={styles.siteEditorRow}>
              <Text style={styles.siteEditorLabel}>Site</Text>
              <View style={styles.siteEditorInputWrap}>
                {isWeb ? (
                  React.createElement(
                    'select' as any,
                    {
                      value: selectedSiteId,
                      onChange: (event: any) => setSelectedSiteId(String(event?.target?.value ?? '')),
                      disabled: formReadOnly,
                      style: webSelectStyle,
                      'aria-label': 'SRA site',
                    },
                    resolvedSiteOptions.map((option) => (
                      React.createElement('option' as any, { key: option.siteId, value: option.siteId }, option.siteName)
                    ))
                  )
                ) : (
                  <Pressable onPress={cycleSite} style={styles.sitePickerBtn} accessibilityRole="button" accessibilityLabel="SRA site" disabled={formReadOnly}>
                    <Text style={styles.sitePickerBtnText} numberOfLines={1}>{resolveSiteNameById(selectedSiteId)}</Text>
                    <Text style={styles.sitePickerChevron}>▼</Text>
                  </Pressable>
                )}
              </View>

              {hasPassword ? (
                <>
                  <Text style={[styles.siteEditorLabel, styles.passwordLabel]}>Password</Text>
                  <TextInput
                    style={[styles.passwordInput, formReadOnly && styles.inlineInputDisabled]}
                    value={passwordValue}
                    onChangeText={setPasswordValue}
                    editable={!formReadOnly}
                    autoCapitalize="none"
                    autoCorrect={false}
                    accessibilityLabel="SRA password"
                  />
                </>
              ) : null}
            </View>

            {isMasterAccount && isCurrentLoggedInSra && canReassignMaster && masterReassignCandidates.length > 0 ? (
              <View style={[styles.siteEditorRow, styles.reassignEditorRow]}>
                <Text style={[styles.siteEditorLabel, styles.reassignEditorLabel]}>Reassign Master Account</Text>
                <View style={[styles.siteEditorInputWrap, styles.reassignEditorInputWrap]}>
                  {isWeb ? (
                    React.createElement(
                      'select' as any,
                      {
                        value: reassignMasterToSraId,
                        onChange: (event: any) => setReassignMasterToSraId(String(event?.target?.value ?? '')),
                        disabled: formReadOnly,
                        style: webSelectStyle,
                        'aria-label': 'Reassign master account',
                      },
                      React.createElement('option' as any, { key: '__none__', value: '' }, 'Keep current master'),
                      masterReassignCandidates.map((option) => (
                        React.createElement('option' as any, { key: option.sraId, value: option.sraId }, removeAccessTypeSuffix(option.label))
                      ))
                    )
                  ) : (
                    <Pressable
                      onPress={cycleReassignMasterTarget}
                      style={styles.sitePickerBtn}
                      accessibilityRole="button"
                      accessibilityLabel="Reassign master account"
                      disabled={formReadOnly}
                    >
                      <Text style={styles.sitePickerBtnText} numberOfLines={1}>
                        {reassignMasterToSraId
                          ? removeAccessTypeSuffix(masterReassignCandidates.find((entry) => entry.sraId === reassignMasterToSraId)?.label || reassignMasterToSraId)
                          : 'Keep current master'}
                      </Text>
                      <Text style={styles.sitePickerChevron}>▼</Text>
                    </Pressable>
                  )}
                </View>
              </View>
            ) : null}
          </View>

        </View>
      ) : null}
    </>
  );
};

const styles = StyleSheet.create({
  row: {
    minHeight: 30,
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: EDITOR_COLORS.separator,
    backgroundColor: EDITOR_COLORS.background,
  },
  rowExpanded: {
    borderBottomWidth: 0,
  },
  toggleCol: {
    width: '8%',
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingLeft: 8,
  },
  nameCol: {
    width: '24%',
    paddingHorizontal: 8,
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  emailCol: {
    width: '32%',
    paddingHorizontal: 8,
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  lastLoginCol: {
    width: '18%',
    paddingHorizontal: 8,
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  accessTypeCol: {
    width: '18%',
    paddingHorizontal: 8,
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  expandButton: {
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  expandButtonText: {
    fontSize: 15,
    lineHeight: 15,
    color: EDITOR_COLORS.textSecondary,
    fontFamily: 'Arial',
  },
  expandImage: {
    width: 9,
    height: 9,
  },
  primaryText: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
    textAlign: 'left',
  },
  primaryTextDisabled: {
    color: EDITOR_COLORS.textMuted,
    fontStyle: 'italic',
  },
  inlineInput: {
    ...editorText,
    width: '100%',
    height: 26,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 3,
    paddingHorizontal: 7,
    color: EDITOR_COLORS.textPrimary,
    backgroundColor: EDITOR_COLORS.background,
  },
  inlineInputDisabled: {
    color: EDITOR_COLORS.textMuted,
    borderColor: '#d1d5db',
    backgroundColor: '#f8fafc',
  },
  editPanel: {
    borderBottomWidth: 1,
    borderBottomColor: EDITOR_COLORS.separator,
    backgroundColor: EDITOR_COLORS.background,
    flexDirection: 'row',
    alignItems: 'stretch',
    paddingHorizontal: 10,
    paddingTop: 0,
    paddingBottom: 8,
    gap: 12,
  },
  // Content area (access table / assessment list, site, ...) — to the right of the menu.
  editPanelLeft: {
    flex: 1,
    backgroundColor: EDITOR_COLORS.background,
    padding: 10,
  },
  // Action menu (Send Invite / Disable / Update / Delete) down the left side, divided from the
  // content by a right-hand separator.
  editPanelMenu: {
    width: 160,
    backgroundColor: EDITOR_COLORS.background,
    borderRightWidth: 1,
    borderRightColor: EDITOR_COLORS.separator,
    paddingTop: 22,
    paddingRight: 12,
    paddingBottom: 10,
    paddingLeft: 8,
    gap: 6,
  },
  editPanelSubheading: {
    ...editorText,
    marginTop: 3,
    color: EDITOR_COLORS.textMuted,
    marginBottom: 10,
  },
  accessTableWrap: {
    backgroundColor: EDITOR_COLORS.background,
  },
  siteEditorRow: {
    marginTop: 10,
    flexDirection: 'row',
    alignItems: 'center',
  },
  siteEditorLabel: {
    ...editorText,
    width: 50,
    paddingLeft: 15,
    color: EDITOR_COLORS.textPrimary,
  },
  siteEditorInputWrap: {
    width: 220,
    maxWidth: '100%',
  },
  passwordLabel: {
    width: 95,
    paddingLeft: 24,
  },
  passwordInput: {
    ...editorText,
    width: 180,
    height: 28,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 3,
    paddingHorizontal: 7,
    color: EDITOR_COLORS.textPrimary,
    backgroundColor: EDITOR_COLORS.background,
  },
  reassignEditorRow: {
    alignItems: 'center',
  },
  reassignEditorLabel: {
    width: 170,
    lineHeight: 18,
  },
  reassignEditorInputWrap: {
    flex: 1,
    width: 'auto',
    minWidth: 220,
  },
  sitePickerBtn: {
    width: '100%',
    height: 28,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 3,
    paddingHorizontal: 7,
    backgroundColor: EDITOR_COLORS.background,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sitePickerBtnText: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
    flex: 1,
    marginRight: 6,
  },
  sitePickerChevron: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
  },
  accessHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: EDITOR_COLORS.background,
  },
  accessHeaderCell: {
    flex: 1,
    minHeight: 34,
    paddingHorizontal: 8,
    paddingVertical: 4,
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: EDITOR_COLORS.separator,
  },
  accessSpacerCol: {
    width: 18,
    backgroundColor: EDITOR_COLORS.background,
  },
  accessHeaderLabel: {
    ...editorText,
    marginLeft: 8,
    color: EDITOR_COLORS.textPrimary,
  },
  accessDataRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
    backgroundColor: EDITOR_COLORS.background,
  },
  accessDataCell: {
    flex: 1,
    minHeight: 30,
    paddingHorizontal: 8,
    paddingVertical: 4,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: EDITOR_COLORS.background,
  },
  accessItemLabel: {
    ...editorText,
    marginLeft: 8,
    color: EDITOR_COLORS.textSecondary,
  },
  accessItemLabelDisabled: {
    color: EDITOR_COLORS.placeholder,
  },
  checkboxTouch: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Office Online: two columns of checkboxes (assessments | settings).
  officeColumns: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 24,
    paddingTop: 4,
    paddingBottom: 8,
  },
  officeColumn: {
    flex: 1,
    minWidth: 0,
    gap: 8,
  },
  officeColumnHeading: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
    fontWeight: '700',
    marginBottom: 2,
  },
  officeCheckRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  officeCheckLabel: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
    flexShrink: 1,
  },
  officeCodeInput: {
    ...editorText,
    width: 90,
    height: 26,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 3,
    paddingHorizontal: 7,
    color: EDITOR_COLORS.textPrimary,
    backgroundColor: EDITOR_COLORS.background,
  },
  officeEmptyText: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
  },
  menuLinkBtn: {
    minHeight: 26,
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  menuLinkBtnUpdate: {
    marginTop: 14,
  },
  menuLinkBtnStatus: {
    marginTop: 8,
  },
  menuLinkBtnDelete: {
    marginTop: 8,
  },
  menuLinkBtnDisabled: {
    opacity: 0.45,
  },
  menuLinkText: {
    ...editorText,
    color: EDITOR_COLORS.accent,
    textDecorationLine: 'none',
  },
  menuLinkTextDanger: {
    color: EDITOR_COLORS.errorText,
  },
  updateErrorText: {
    ...editorText,
    marginTop: 4,
    color: EDITOR_COLORS.errorText,
    lineHeight: 17,
  },
});
